import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner';
import { fmt } from '../utils/igv';
import ProductForm from '../components/ProductForm';
import api from '../utils/api';

export default function MainPage() {
  const { products, categories, cart, addToCart, setPage } = useApp();
  const [selCat,         setSelCat]         = useState('all');
  const [search,         setSearch]         = useState('');
  const [scanOpen,       setScanOpen]       = useState(false);
  const [scanCode,       setScanCode]       = useState('');
  const [scanHit,        setScanHit]        = useState(null);
  const [detProd,        setDetProd]        = useState(null);
  const [detQty,         setDetQty]         = useState(1);
  const [newProdBarcode, setNewProdBarcode] = useState('');
  const [showNewProd,    setShowNewProd]    = useState(false);

  // Modal de peso
  const [weightProd,  setWeightProd]  = useState(null); // producto esperando peso
  const [weightInput, setWeightInput] = useState('');
  const [weightFrom,  setWeightFrom]  = useState('grid'); // 'grid' | 'detail' | 'scan'
  const [weightMode,  setWeightMode]  = useState('kg');   // 'kg' | 'soles'

  // Botón atrás cierra el detalle en lugar de salir
  useEffect(() => {
    const handleBack = (e) => {
      if (detProd) {
        e.preventDefault();
        setDetProd(null);
      }
    };
    window.addEventListener('popstate', handleBack);
    return () => window.removeEventListener('popstate', handleBack);
  }, [detProd]);

  const { filtered, isDefault } = useMemo(() => {
    const searching = search.trim().length > 0;
    const filtering = selCat !== 'all';

    // Sin búsqueda ni categoría → mostrar 20 más vendidos
    if (!searching && !filtering) {
      const top = [...products]
        .sort((a, b) => (b.sales_count || 0) - (a.sales_count || 0))
        .slice(0, 20);
      return { filtered: top, isDefault: true };
    }

    let list = products;
    if (filtering) list = list.filter(p => p.category_id === selCat);
    if (searching) {
      const lo = search.toLowerCase();
      // Buscar por nombre del producto O por nombre de categoría
      // Buscar categoría con coincidencia flexible:
      // "whiskys" encuentra "Whisky", "rones" encuentra "Ron", etc.
      const catMatch = categories.find(c => {
        const cn = c.name.toLowerCase();
        return cn.includes(lo) || lo.includes(cn) ||
          // coincidencia parcial: al menos 3 chars y uno contiene al otro recortado
          (lo.length >= 3 && (cn.startsWith(lo.slice(0,-1)) || lo.startsWith(cn.slice(0,-1))));
      });
      if (catMatch && !filtering) {
        // Si coincide con una categoría, mostrar todos sus productos
        list = list.filter(p => p.category_id === catMatch.id);
      } else {
        list = list.filter(p => p.name?.toLowerCase().includes(lo));
      }
    }
    return { filtered: list, isDefault: false };
  }, [products, selCat, search, categories]);

  const cartTotal = cart.reduce((s, i) => {
    const price = i.by_weight ? (i.price_per_kg || i.price) : i.price;
    return s + price * i.qty;
  }, 0);
  // Cuenta ítems (líneas), no unidades — así los productos por peso no dan decimales
  const cartCount = cart.length;

  const onDetected = useCallback(async (code) => {
    try {
      const { product } = await api.getByBarcode(code);
      setScanHit(product);
    } catch (e) {
      if (e.status === 404) {
        setNewProdBarcode(code);
        setScanOpen(false);
        setShowNewProd(true);
      }
    }
  }, []);

  const { scanning, error: camErr } = useBarcodeScanner(onDetected, scanOpen);

  const handleScanInput = (val) => {
    setScanCode(val); setScanHit(null);
    if (!val.trim()) return;
    const lo = val.toLowerCase();
    const local = products.find(p => p.barcode === val || p.name.toLowerCase().includes(lo));
    if (local) setScanHit(local);
  };

  const handleScanOk = () => {
    if (!scanCode.trim()) return;
    const lo = scanCode.toLowerCase();
    const local = products.find(p => p.barcode === scanCode || p.name.toLowerCase().includes(lo));
    if (local) { setScanHit(local); return; }
    setNewProdBarcode(scanCode);
    setScanOpen(false);
    setScanCode('');
    setShowNewProd(true);
  };

  // Abre modal de peso o agrega directo según tipo de producto
  const tryAddToCart = useCallback((p, from = 'grid') => {
    if (p.by_weight) {
      setWeightProd(p);
      setWeightInput('');
      setWeightFrom(from);
      setWeightMode('kg');
    } else {
      addToCart({
        id: p.id, name: p.name, price: p.price,
        qty: 1, emoji: p.emoji || '📦',
        unit: p.unit || 'unid', image_url: p.image_url,
        by_weight: false,
      });
    }
  }, [addToCart]);

  const confirmWeight = () => {
    const val = parseFloat(weightInput.replace(',', '.'));
    if (!isNaN(val) && val > 0 && weightProd) {
      // Si modo soles: calcular kg equivalentes; si modo kg: usar directo
      const kg    = weightMode === 'soles' ? val / weightProd.price : val;
      const total = weightMode === 'soles' ? val : val * weightProd.price;
      addToCart({
        id: weightProd.id,
        name: weightProd.name,
        price: weightProd.price,        // precio por kg
        price_per_kg: weightProd.price, // guardamos referencia
        qty: kg,
        total_override: total,          // monto fijo si se ingresó en soles
        emoji: weightProd.emoji || '📦',
        unit: 'kg',
        image_url: weightProd.image_url,
        by_weight: true,
        manual: true,
      });
      if (weightFrom === 'detail') setDetProd(null);
      if (weightFrom === 'scan')   { setScanHit(null); setScanCode(''); }
    }
    setWeightProd(null);
    setWeightInput('');
  };

  const addFromScan = (p) => {
    if (p.by_weight) {
      setWeightProd(p);
      setWeightInput('');
      setWeightFrom('scan');
    } else {
      addToCart({ id: p.id, name: p.name, price: p.price, qty: 1, emoji: p.emoji || '📦', unit: p.unit || 'unid', image_url: p.image_url, by_weight: false });
      setScanHit(null);
      setScanCode('');
    }
  };

  const closeScan = () => { setScanOpen(false); setScanCode(''); setScanHit(null); };

  const openDetail = (p) => {
    setDetProd(p);
    setDetQty(1);
    window.history.pushState({ detail: p.id }, '');
  };

  const addFromDetail = () => {
    if (detProd.by_weight) {
      setWeightProd(detProd);
      setWeightInput('');
      setWeightFrom('detail');
    } else {
      addToCart({ id: detProd.id, name: detProd.name, price: detProd.price, qty: detQty, emoji: detProd.emoji || '📦', unit: detProd.unit || 'unid', image_url: detProd.image_url, by_weight: false });
      setDetProd(null);
    }
  };

  const allCats = [{ id: 'all', name: 'Todos', emoji: '🏪' }, ...categories];

  return (
    <>
      <div style={S.wrap}>
        <div style={S.topbar}>
          <div style={S.srow}>
            <div style={S.sbox}>
              <span style={S.sico}>🔍</span>
              <input style={S.sinp} placeholder="Buscar producto..." value={search}
                onChange={e => setSearch(e.target.value)} autoComplete="off"/>
              {search && <button style={S.clearBtn} onClick={() => setSearch('')}>✕</button>}
            </div>
            <button style={S.scanBtn} onClick={() => setScanOpen(true)}>📷</button>
          </div>
        </div>

        <div style={S.catsWrap}>
          <div style={S.catsRow}>
            {allCats.map(c => (
              <div key={c.id} style={S.catItem} onClick={() => { setSelCat(c.id); setSearch(''); }}>
                <div style={{ ...S.cball, ...(selCat === c.id ? S.cballOn : {}), overflow:'hidden' }}>
                  {c.image_url
                    ? <img src={c.image_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:14 }}/>
                    : c.emoji
                  }
                </div>
                <span style={{ ...S.clbl, ...(selCat === c.id ? S.clblOn : {}) }}>{c.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={S.prodsWrap}>
          <div style={S.secHdr}>
            <span style={S.secNm}>
              {isDefault
                ? '⭐ Más vendidos'
                : selCat !== 'all'
                  ? (allCats.find(c => c.id === selCat)?.emoji + ' ' + allCats.find(c => c.id === selCat)?.name)
                  : (() => {
                      const lo = search.toLowerCase();
                      const catMatch = categories.find(c => c.name.toLowerCase().includes(lo));
                      return catMatch ? `${catMatch.emoji} ${catMatch.name}` : 'Resultados';
                    })()
              }
            </span>
            <span style={S.secCt}>
              {isDefault ? `${filtered.length} sugeridos` : `${filtered.length} producto${filtered.length !== 1 ? 's' : ''}`}
            </span>
          </div>
          <div style={S.pgrid}>
            {filtered.length === 0
              ? <div style={S.noRes}>
                  {search.trim() ? 'Sin resultados para "' + search + '"' : 'Sin productos en esta categoría'}
                </div>
              : filtered.map(p => (
                  <ProductCard key={p.id} p={p}
                    onTap={() => openDetail(p)}
                    onQuickAdd={() => tryAddToCart(p, 'grid')}
                  />
                ))
            }
          </div>
        </div>

        {cart.length > 0 && (
          <div style={S.cstrip} onClick={() => setPage('cart')}>
            <span style={S.csBadge}>{cartCount}</span>
            <span style={S.csInfo}>{cart.length} producto{cart.length > 1 ? 's' : ''}</span>
            <span style={S.csTotal}>{fmt(cartTotal)}</span>
            <span style={S.csArr}>›</span>
          </div>
        )}
      </div>

      {/* SCANNER */}
      {scanOpen && (
        <div style={S.scanOverlay}>
          <div style={S.scanSheet}>
            <div style={S.camBox}>
              <div id="barcode-reader" style={{ width:'100%', height:'100%', background:'#000' }}/>
              <div style={S.scanFrameWrap}>
                <div style={S.scanFrame}>
                  {scanning && <div style={S.scanLine}/>}
                </div>
                <p style={S.scanHint}>{camErr || (scanning ? 'Apunta al código de barras' : 'Iniciando cámara...')}</p>
                <div style={S.floatBtns}>
                  <button style={S.floatCartBtn} onClick={() => { closeScan(); setPage('cart'); }}>
                    🛒 Ver carrito {cartCount > 0 && <span style={S.floatBadge}>{cartCount}</span>}
                  </button>
                  <button style={S.floatCloseBtn} onClick={closeScan}>✕ Salir</button>
                </div>
              </div>
            </div>

            {scanHit && (
              <div style={S.hitOverlay}>
                <div style={S.hitCard}>
                  <div style={S.hitImg}>
                    {scanHit.image_url
                      ? <img src={scanHit.image_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:16 }}/>
                      : <span style={{ fontSize:56 }}>{scanHit.emoji || '📦'}</span>
                    }
                  </div>
                  <p style={{ fontSize:22, fontWeight:900, textAlign:'center', lineHeight:1.2, marginBottom:4 }}>{scanHit.name}</p>
                  <p style={{ fontSize:13, color:'var(--t1)', textAlign:'center', marginBottom:4 }}>{scanHit.category_name || 'Sin categoría'}</p>
                  <p style={{ fontSize:13, color:'var(--t1)', textAlign:'center', marginBottom:12 }}>
                    Stock: <span style={{ color: scanHit.stock <= (scanHit.min_stock||5) ? 'var(--err)' : 'var(--ok)', fontWeight:700 }}>{scanHit.stock}</span> {scanHit.unit}
                  </p>
                  <p style={{ fontSize:38, fontWeight:900, color:'var(--acc)', textAlign:'center', fontFamily:'var(--mono)', marginBottom:4 }}>{fmt(scanHit.price)}</p>
                  {scanHit.by_weight && (
                    <p style={{ fontSize:12, color:'var(--t1)', textAlign:'center', marginBottom:16 }}>⚖️ precio por kg</p>
                  )}
                  <button style={S.hitAddBtn} onClick={() => addFromScan(scanHit)}>
                    {scanHit.by_weight ? '⚖️ Ingresar peso' : '＋ Agregar al carrito'}
                  </button>
                  <button style={S.hitScanBtn} onClick={() => setScanHit(null)}>📷 Volver a escanear</button>
                  <button style={S.hitCloseBtn} onClick={closeScan}>✕ Salir</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* FORMULARIO para código nuevo */}
      {showNewProd && (
        <ProductForm
          prefillBarcode={newProdBarcode}
          onClose={() => { setShowNewProd(false); setNewProdBarcode(''); }}
        />
      )}

      {/* DETALLE DE PRODUCTO */}
      {detProd && (
        <div style={S.detOverlay}>
          <div style={S.detPanel}>
            <div style={S.detImg}>
              <button style={S.detBack} onClick={() => setDetProd(null)}>← Volver</button>
              {detProd.image_url
                ? <img src={detProd.image_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                : <span style={{ fontSize:88 }}>{detProd.emoji || '📦'}</span>
              }
            </div>
            <div style={S.detBody}>
              <h2 style={S.detName}>{detProd.name}</h2>
              <p style={{ fontSize:36, fontWeight:900, color:'var(--acc)', marginBottom:4, fontFamily:'var(--mono)' }}>
                {fmt(detProd.price)}{detProd.by_weight ? <span style={{ fontSize:14, fontWeight:400, color:'var(--t1)' }}> / kg</span> : ''}
              </p>
              {detProd.by_weight && (
                <p style={{ fontSize:12, color:'#f0b400', background:'rgba(255,184,0,.1)', border:'0.5px solid rgba(255,184,0,.3)', borderRadius:7, padding:'4px 10px', display:'inline-block', marginBottom:12 }}>
                  ⚖️ Producto vendido por peso
                </p>
              )}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
                <Stat val={`${detProd.stock} ${detProd.unit}`} lbl="Stock disponible" color={detProd.stock <= (detProd.min_stock || 5) ? 'var(--err)' : 'var(--ok)'}/>
                <Stat val={`${detProd.min_stock || 5} ${detProd.unit}`} lbl="Stock mínimo" color="var(--t1)"/>
              </div>
              {detProd.barcode && <DetRow lbl="Código" val={<span style={{ fontFamily:'monospace', fontSize:12 }}>{detProd.barcode}</span>}/>}
              {detProd.cost > 0 && <>
                <DetRow lbl="Costo"  val={fmt(detProd.cost)}/>
                <DetRow lbl="Margen" val={<span style={{ color:'var(--teal)' }}>{(((detProd.price - detProd.cost) / detProd.cost) * 100).toFixed(1)}%</span>}/>
              </>}
              {detProd.description && <div style={S.detDesc}>{detProd.description}</div>}
            </div>
            <div style={S.detFoot}>
              {!detProd.by_weight && (
                <div style={S.detQC}>
                  <button style={S.dqBtn} onClick={() => setDetQty(q => Math.max(1, q - 1))}>−</button>
                  <span style={S.dqNum}>{detQty}</span>
                  <button style={S.dqBtn} onClick={() => setDetQty(q => q + 1)}>+</button>
                </div>
              )}
              <button style={S.detAddBtn} onClick={addFromDetail}>
                {detProd.by_weight ? '⚖️ Ingresar peso' : '+ Agregar al carrito'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL INGRESO DE PESO */}
      {weightProd && (() => {
        const val     = parseFloat(weightInput.replace(',','.'));
        const valid   = !isNaN(val) && val > 0;
        const kg      = valid ? (weightMode === 'soles' ? val / weightProd.price : val) : 0;
        const total   = valid ? (weightMode === 'soles' ? val : val * weightProd.price) : 0;
        return (
          <div style={S.weightOverlay} onClick={() => setWeightProd(null)}>
            <div style={S.weightBox} onClick={e => e.stopPropagation()}>
              {/* Cabecera producto */}
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                <div style={{ width:44, height:44, background:'var(--bg2)', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', flexShrink:0 }}>
                  {weightProd.image_url
                    ? <img src={weightProd.image_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                    : <span style={{ fontSize:24 }}>{weightProd.emoji || '📦'}</span>
                  }
                </div>
                <div>
                  <p style={{ fontSize:15, fontWeight:700, color:'var(--t0)' }}>{weightProd.name}</p>
                  <p style={{ fontSize:12, color:'var(--t1)' }}>{fmt(weightProd.price)} por kg</p>
                </div>
              </div>

              {/* Selector kg / soles */}
              <div style={S.wTabs}>
                <button style={{ ...S.wTab, ...(weightMode==='kg' ? S.wTabOn : {}) }}
                  onMouseDown={e => { e.preventDefault(); setWeightMode('kg'); setWeightInput(''); }}>
                  <span style={{ fontSize:20 }}>⚖️</span>
                  <span style={{ fontSize:14, fontWeight:800 }}>Kilos</span>
                </button>
                <button style={{ ...S.wTab, ...(weightMode==='soles' ? S.wTabOn : {}) }}
                  onMouseDown={e => { e.preventDefault(); setWeightMode('soles'); setWeightInput(''); }}>
                  <span style={{ fontSize:20 }}>💰</span>
                  <span style={{ fontSize:14, fontWeight:800 }}>Soles</span>
                </button>
              </div>

              <label style={S.wLabel}>
                {weightMode === 'kg' ? '⚖️ Ingresa el peso' : '💰 Ingresa el monto'}
              </label>
              <div style={S.wInputWrap}>
                <input
                  style={S.wInput}
                  type="number"
                  step={weightMode === 'kg' ? '0.001' : '0.10'}
                  min={weightMode === 'kg' ? '0.001' : '0.10'}
                  placeholder={weightMode === 'kg' ? '0.000' : '0.00'}
                  value={weightInput}
                  onChange={e => setWeightInput(e.target.value)}
                  autoFocus
                />
                <span style={S.wUnit}>{weightMode === 'kg' ? 'kg' : 'S/'}</span>
              </div>

              {valid && (
                <div style={S.wPreview}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ color:'var(--t1)', fontSize:12 }}>Peso:</span>
                    <span style={{ fontWeight:700, fontSize:14 }}>{kg.toFixed(3)} kg</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:4 }}>
                    <span style={{ color:'var(--t1)', fontSize:12 }}>Total:</span>
                    <span style={{ fontWeight:800, fontSize:20, color:'var(--acc)' }}>{fmt(total)}</span>
                  </div>
                </div>
              )}

              <div style={{ display:'flex', gap:10, marginTop:16 }}>
                <button style={S.wCancel} onClick={() => setWeightProd(null)}>Cancelar</button>
                <button style={S.wConfirm} onClick={confirmWeight} disabled={!valid}>
                  ✓ Agregar al carrito
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}

function ProductCard({ p, onTap, onQuickAdd }) {
  const low = p.stock <= (p.min_stock || 5);

  const expiryDays = p.expiry_date
    ? Math.ceil((new Date(p.expiry_date) - new Date()) / 86400000)
    : null;
  const alertDays  = p.expiry_alert_days || 7;
  const isExpired  = expiryDays !== null && expiryDays < 0;
  const isExpiring = expiryDays !== null && !isExpired && expiryDays <= alertDays;
  const expiryMsg  = isExpired
    ? `Vencido hace ${Math.abs(expiryDays)}d`
    : expiryDays === 0 ? 'Vence hoy'
    : `Vence en ${expiryDays}d`;

  const borderColor = isExpired ? 'rgba(231,76,60,.6)' : isExpiring ? 'rgba(243,156,18,.5)' : low ? 'rgba(231,76,60,.4)' : 'var(--bd)';

  return (
    <div style={{ background:'var(--bg2)', borderRadius:12, border:`0.5px solid ${borderColor}`, overflow:'hidden' }}>
      <div style={{ width:'100%', aspectRatio:'1/1', background:'var(--bg3)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', position:'relative' }} onClick={onTap}>
        {p.image_url ? <img src={p.image_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/> : <span style={{ fontSize:30 }}>{p.emoji || '📦'}</span>}
        {low && <div style={{ position:'absolute', top:5, left:5, width:8, height:8, borderRadius:4, background:'var(--err)' }}/>}
        {(isExpired || isExpiring) && (
          <div style={{ position:'absolute', top:0, left:0, right:0, background: isExpired ? 'rgba(231,76,60,.85)' : 'rgba(243,156,18,.85)', padding:'3px 6px', fontSize:9, fontWeight:800, color:'#fff', textAlign:'center' }}>
            📅 {expiryMsg}
          </div>
        )}
        <button style={{ position:'absolute', bottom:5, right:5, width:26, height:26, background:'var(--acc)', borderRadius:'50%', border:'none', color:'#fff', fontSize:20, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', lineHeight:1 }}
          onClick={e => { e.stopPropagation(); onQuickAdd(); }}>{p.by_weight ? '⚖' : '+'}</button>
      </div>
      <div style={{ padding:'6px 7px 8px', cursor:'pointer' }} onClick={onTap}>
        <p style={{ fontSize:10, fontWeight:600, lineHeight:1.3, marginBottom:3, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>{p.name}</p>
        <p style={{ fontSize:13, fontWeight:800, color:'var(--acc)' }}>
          {fmt(p.price)}{p.by_weight ? <span style={{ fontSize:9, fontWeight:400, color:'var(--t1)' }}>/kg</span> : ''}
        </p>
        <p style={{ fontSize:9, color: low ? 'var(--err)' : 'var(--t2)', marginTop:1 }}>{p.stock} {p.unit}{low ? ' ⚠' : ''}</p>
      </div>
    </div>
  );
}

function Stat({ val, lbl, color }) {
  return (
    <div style={{ background:'var(--bg2)', borderRadius:10, padding:'10px 8px', textAlign:'center', border:'0.5px solid var(--bd)' }}>
      <div style={{ fontSize:16, fontWeight:800, marginBottom:2, color }}>{val}</div>
      <div style={{ fontSize:9, color:'var(--t1)', textTransform:'uppercase', letterSpacing:.4 }}>{lbl}</div>
    </div>
  );
}

function DetRow({ lbl, val }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'9px 0', borderBottom:'0.5px solid var(--bd)', fontSize:13 }}>
      <span style={{ color:'var(--t1)' }}>{lbl}</span>
      <span style={{ fontWeight:600 }}>{val}</span>
    </div>
  );
}

const S = {
  wrap:       { display:'flex', flexDirection:'column', height:'100%', overflow:'hidden' },
  topbar:     { padding:'10px 12px 8px', flexShrink:0 },
  srow:       { display:'flex', gap:8, alignItems:'stretch' },
  sbox:       { flex:1, display:'flex', alignItems:'center', gap:8, background:'var(--bg2)', border:'0.5px solid var(--bd2)', borderRadius:12, padding:'0 12px', position:'relative' },
  sico:       { fontSize:15, color:'var(--t2)' },
  sinp:       { flex:1, background:'none', border:'none', color:'var(--t0)', fontSize:13 },
  clearBtn:   { background:'none', border:'none', color:'var(--t2)', fontSize:14, padding:2 },
  scanBtn:    { flex:'0 0 30%', background:'var(--acc)', borderRadius:14, display:'flex', alignItems:'center', justifyContent:'center', fontSize:54, border:'none', lineHeight:1, padding:0, overflow:'hidden', paddingBottom:10 },
  catsWrap:   { flexShrink:0, padding:'4px 0 6px' },
  catsRow:    { display:'flex', gap:8, padding:'0 12px', overflowX:'auto', scrollbarWidth:'none' },
  catItem:    { display:'flex', flexDirection:'column', alignItems:'center', gap:3, flexShrink:0, cursor:'pointer' },
  cball:      { width:54, height:54, borderRadius:16, background:'var(--bg2)', border:'1.5px solid var(--bd2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:23 },
  cballOn:    { background:'rgba(232,67,90,.18)', borderColor:'var(--acc)' },
  clbl:       { fontSize:9, color:'var(--t1)', fontWeight:500, maxWidth:56, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', textAlign:'center' },
  clblOn:     { color:'var(--acc)', fontWeight:700 },
  prodsWrap:  { flex:1, overflowY:'auto', padding:'4px 12px 6px', scrollbarWidth:'none' },
  secHdr:     { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 },
  secNm:      { fontSize:11, fontWeight:700, color:'var(--t1)', textTransform:'uppercase', letterSpacing:.5 },
  secCt:      { fontSize:10, color:'var(--t2)' },
  pgrid:      { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 },
  noRes:      { gridColumn:'1/-1', textAlign:'center', padding:'40px 0', color:'var(--t2)', fontSize:13 },
  cstrip:     { background:'var(--bg1)', borderTop:'0.5px solid var(--bd)', padding:'8px 14px', cursor:'pointer', flexShrink:0, display:'flex', alignItems:'center', gap:8 },
  csBadge:    { background:'var(--acc)', color:'#fff', borderRadius:10, padding:'2px 9px', fontSize:11, fontWeight:800 },
  csInfo:     { flex:1, fontSize:12, color:'var(--t1)' },
  csTotal:    { fontSize:16, fontWeight:800, color:'var(--acc)' },
  csArr:      { fontSize:16, color:'var(--t2)' },
  scanOverlay:   { position:'fixed', inset:0, background:'#000', display:'flex', flexDirection:'column', zIndex:60 },
  scanSheet:     { display:'flex', flexDirection:'column', flex:1 },
  camBox:        { flex:1, position:'relative', overflow:'hidden' },
  scanFrameWrap: { position:'absolute', inset:0, zIndex:10, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:16, pointerEvents:'none' },
  scanFrame:     { width:250, height:250, position:'relative', border:'2px solid rgba(232,67,90,.6)', borderRadius:12 },
  scanLine:      { position:'absolute', left:8, right:8, height:3, background:'var(--acc)', animation:'scanLine 1.8s ease-in-out infinite', borderRadius:2 },
  scanHint:      { fontSize:13, color:'rgba(255,255,255,.85)', background:'rgba(0,0,0,.55)', padding:'6px 18px', borderRadius:20, whiteSpace:'nowrap' },
  hitOverlay:    { position:'absolute', inset:0, zIndex:20, background:'rgba(0,0,0,.92)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 },
  hitCard:       { background:'var(--bg1)', borderRadius:24, padding:24, width:'100%', maxWidth:380, display:'flex', flexDirection:'column', alignItems:'center', border:'1.5px solid var(--acc)' },
  hitImg:        { width:110, height:110, background:'var(--bg2)', borderRadius:16, display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', marginBottom:16 },
  hitAddBtn:     { width:'100%', background:'var(--acc)', color:'#fff', border:'none', borderRadius:14, padding:'15px', fontSize:17, fontWeight:900, marginBottom:10 },
  hitScanBtn:    { width:'100%', background:'var(--bg2)', color:'var(--t0)', border:'0.5px solid var(--bd2)', borderRadius:14, padding:'13px', fontSize:15, fontWeight:600, marginBottom:8 },
  hitCloseBtn:   { width:'100%', background:'none', color:'var(--t2)', border:'none', borderRadius:14, padding:'10px', fontSize:13 },
  scanAddBtn:    { background:'var(--acc)', color:'#fff', border:'none', borderRadius:12, padding:'12px 16px', fontSize:15, fontWeight:900, flexShrink:0, lineHeight:1.4, textAlign:'center' },
  scanInp:       { flex:1, background:'var(--bg2)', border:'0.5px solid var(--bd2)', borderRadius:9, padding:'9px 12px', color:'var(--t0)', fontSize:13 },
  scanOkBtn:     { background:'var(--acc)', color:'#fff', border:'none', borderRadius:9, padding:'9px 16px', fontSize:13, fontWeight:700 },
  floatBtns:     { display:'flex', flexDirection:'column', gap:10, marginTop:30, pointerEvents:'all', width:'72vw', maxWidth:300 },
  floatCartBtn:  { background:'rgba(255,255,255,.18)', backdropFilter:'blur(8px)', color:'#fff', border:'1px solid rgba(255,255,255,.3)', borderRadius:14, padding:'14px 20px', fontSize:16, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', gap:8 },
  floatCloseBtn: { background:'rgba(0,0,0,.5)', backdropFilter:'blur(8px)', color:'rgba(255,255,255,.8)', border:'1px solid rgba(255,255,255,.15)', borderRadius:14, padding:'14px 20px', fontSize:16, fontWeight:600, textAlign:'center' },
  floatBadge:    { background:'var(--acc)', color:'#fff', borderRadius:8, padding:'1px 7px', fontSize:11, fontWeight:800 },
  scanClose:     { width:'100%', background:'none', border:'none', color:'var(--t2)', fontSize:12, padding:6 },
  detOverlay:    { position:'fixed', inset:0, zIndex:50, background:'var(--bg0)', display:'flex', flexDirection:'column', animation:'slideIn .25s ease' },
  detPanel:      { display:'flex', flexDirection:'column', height:'100%' },
  detImg:        { width:'100%', height:'45vh', background:'var(--bg2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, position:'relative', overflow:'hidden' },
  detBack:       { position:'absolute', top:12, left:12, background:'rgba(0,0,0,.55)', border:'none', borderRadius:10, padding:'6px 12px', color:'#fff', fontSize:13, zIndex:1 },
  detBody:       { flex:1, overflowY:'auto', padding:16, scrollbarWidth:'none' },
  detName:       { fontSize:22, fontWeight:800, marginBottom:8 },
  detCatBadge:   { display:'inline-block', background:'rgba(232,67,90,.12)', color:'var(--acc)', borderRadius:6, padding:'3px 10px', fontSize:11, fontWeight:600, marginBottom:14 },
  detStats:      { display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:14 },
  detDesc:       { marginTop:12, background:'var(--bg2)', borderRadius:10, padding:12, fontSize:12, color:'var(--t1)', lineHeight:1.6 },
  detFoot:       { padding:'12px 16px', borderTop:'0.5px solid var(--bd)', display:'flex', gap:10, flexShrink:0 },
  detQC:         { display:'flex', alignItems:'center', gap:8, background:'var(--bg2)', borderRadius:10, padding:'0 10px', border:'0.5px solid var(--bd2)' },
  dqBtn:         { background:'none', border:'none', color:'var(--t0)', fontSize:20, padding:'8px 4px' },
  dqNum:         { fontSize:15, fontWeight:700, minWidth:24, textAlign:'center' },
  detAddBtn:     { flex:1, background:'var(--acc)', color:'#fff', border:'none', borderRadius:10, padding:12, fontSize:14, fontWeight:800 },
  // Modal peso
  weightOverlay: { position:'fixed', inset:0, background:'rgba(0,0,0,.8)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:20 },
  weightBox:     { background:'var(--bg1)', borderRadius:18, padding:22, width:'100%', maxWidth:320, border:'0.5px solid var(--bd2)' },
  wLabel:        { display:'block', fontSize:11, color:'var(--t1)', fontWeight:600, textTransform:'uppercase', letterSpacing:.4, marginBottom:8 },
  wInput:        { width:'100%', background:'var(--bg2)', border:'1.5px solid var(--acc)', borderRadius:12, padding:'14px 52px 14px 14px', color:'var(--t0)', fontSize:28, fontWeight:800, textAlign:'center' },
  wPreview:      { display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:12, background:'var(--bg2)', borderRadius:10, padding:'10px 14px', border:'0.5px solid var(--bd2)' },
  wTabs:         { display:'flex', gap:6, marginBottom:12, background:'var(--bg2)', borderRadius:10, padding:4 },
  wTab:          { flex:1, background:'none', border:'none', borderRadius:8, padding:'10px 6px', fontSize:18, fontWeight:700, color:'var(--t2)', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:3 },
  wTabOn:        { background:'var(--acc)', color:'#fff', boxShadow:'0 2px 8px rgba(232,67,90,.4)' },
  wInputWrap:    { position:'relative', display:'flex', alignItems:'center' },
  wUnit:         { position:'absolute', right:14, fontSize:20, fontWeight:800, color:'var(--acc)', pointerEvents:'none' },
  wCancel:       { flex:1, background:'var(--bg2)', color:'var(--t1)', border:'0.5px solid var(--bd)', borderRadius:10, padding:12, fontSize:14 },
  wConfirm:      { flex:2, background:'var(--acc)', color:'#fff', border:'none', borderRadius:10, padding:12, fontSize:14, fontWeight:700 },
};
